<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore, type User } from '@/stores/auth';
import { useSidebarStore } from '@/stores/sidebar';
import { usePaletteStore } from '@/stores/palette';
import { useVisualViewport } from '@/composables/useVisualViewport';
import { useI18n } from '@/lib/i18n';
import AppNav from './AppNav.vue';
import NavDrawer from './NavDrawer.vue';
import AppIcon from './AppIcon.vue';
import ConfirmModal from './ConfirmModal.vue';
import PromptModal from './PromptModal.vue';
import ToastStack from './ToastStack.vue';
import Lightbox from './Lightbox.vue';
import QuickAsk from './QuickAsk.vue';
import CommandPalette from './CommandPalette.vue';

const props = defineProps<{ user: User | null }>();

const auth = useAuthStore();
const sidebar = useSidebarStore();
const palette = usePaletteStore();
const route = useRoute();
const { t } = useI18n();

// Publish keyboard-aware viewport metrics (--vvh / --kb) so overlays can size
// themselves above the on-screen keyboard. App-wide, set once here.
useVisualViewport();

// Workspace label shown in the phone top bar (the desktop nav carries the full
// switcher; the bar just needs an identity anchor). Falls back to the nav's
// generic name if the tenant isn't loaded yet.
const workspaceName = computed(
  () => props.user?.tenant?.name ?? t('nav.ws.fallback_name'),
);

// ⌘K / Ctrl-K toggles the command palette from anywhere in the app. Quick ask
// is now the palette's first command rather than the direct Cmd-K target.
// (The nav drawer is a Reka Dialog now, so it owns its own Escape-to-close.)
function onGlobalKey(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault();
    palette.toggle();
  }
}

// Tap-outside-to-dismiss for touch. On iOS, tapping a non-focusable element
// doesn't move focus, so a focused input / chat composer keeps the on-screen
// keyboard up with no obvious way out. When a touch lands OUTSIDE any editable
// or interactive control, blur the active editable so the keyboard dismisses.
// Touch-only (desktop already blurs on click-outside); interactive targets are
// skipped so buttons/links/the composer's own controls behave normally.
const DISMISS_SKIP = 'input, textarea, select, [contenteditable], button, a, label, [role="button"], [role="menuitem"], [role="tab"], [role="switch"]';
function onPointerDownDismiss(e: PointerEvent): void {
  if (e.pointerType !== 'touch') return;
  const active = document.activeElement as HTMLElement | null;
  if (!active || !(active.matches('input, textarea') || active.isContentEditable)) return;
  const target = e.target as Element | null;
  if (target && target.closest(DISMISS_SKIP)) return;
  active.blur();
}

// Navigating (tapping a drawer link, a workspace switch, anything that changes
// the route) dismisses the drawer so the destination isn't hidden behind it.
// (Outside-tap, Escape, scroll-lock and focus are the Dialog's job - see
// NavDrawer.vue; this only covers the route-change case Reka can't know about.)
watch(() => route.fullPath, () => sidebar.closeMobile());

// Routes can opt-in to "full bleed" via `meta.fullBleed = true`.
// We drop the max-width cap and outer padding on the content wrapper
// so the view fills the shell column edge-to-edge and can own its
// own internal scroll regions (e.g. Studio's two-pane layout).
const fullBleed = computed(() =>
  Boolean(route.meta.fullBleed) || route.name === 'studio'
);
const navColumnWidth = computed(() =>
  sidebar.collapsed ? 'var(--nav-width-rail)' : 'var(--nav-width)',
);
onMounted(() => {
  auth.hydrate();
  window.addEventListener('keydown', onGlobalKey);
  window.addEventListener('pointerdown', onPointerDownDismiss, { passive: true });
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKey);
  window.removeEventListener('pointerdown', onPointerDownDismiss);
});
</script>

<template>
  <div
    class="shell"
    :style="{ '--current-nav-width': navColumnWidth }"
  >
    <!-- Desktop: the nav is a grid column (rail / expanded). On phones the
         column collapses (grid-template-columns: 1fr below --bp-sm) and the
         nav moves into NavDrawer (a Reka Dialog sheet, rendered below). -->
    <AppNav v-if="!sidebar.isMobile" class="shell__nav" :user="user" />
    <main class="shell__main" :class="{ 'shell__main--full': fullBleed }">
      <!-- Phone top bar: the only on-screen route to navigation once the nav
           collapses to a drawer (<--bp-sm). Hamburger toggles the drawer; the
           search button reaches the command palette (no Cmd-K on touch). -->
      <header v-if="sidebar.isMobile" class="shell__mobilebar">
        <button
          class="shell__mobilebar-btn"
          type="button"
          :aria-label="t('nav.menu.open')"
          :aria-expanded="sidebar.mobileOpen"
          @click="sidebar.openMobile()"
        >
          <AppIcon name="list" :size="20" />
        </button>
        <span class="shell__mobilebar-title">{{ workspaceName }}</span>
        <button
          class="shell__mobilebar-btn"
          type="button"
          :aria-label="t('nav.search.label')"
          @click="palette.openPanel()"
        >
          <AppIcon name="magnifying-glass" :size="20" />
        </button>
      </header>
      <!-- For full-bleed routes the view fills the column edge-to-edge and
           owns its own scroll. It's wrapped in a relative, flex:1 box so a
           view positioned `absolute; inset: 0` (Documents, Email message) or
           sized `height: 100%` (Studio) resolves against the area BELOW the
           phone top bar, not the raw viewport - otherwise the bar would
           overlap or clip it. -->
      <template v-if="fullBleed">
        <div class="shell__bleed">
          <slot />
        </div>
      </template>
      <template v-else>
        <!-- Fixed header row. PageHeader teleports its bar in here so the
             content scrollbar below starts UNDER the header, not running
             the full height alongside it. Empty (0-height) for the rare
             view that renders no PageHeader. -->
        <div id="app-header" class="shell__header"></div>
        <div class="shell__content">
          <slot />
        </div>
      </template>
    </main>
    <!-- Mobile nav drawer: AppNav in a Reka Dialog sheet. Portals to <body>;
         rendered only on phones, opened from the top bar's hamburger. -->
    <NavDrawer v-if="sidebar.isMobile" :user="user" />
    <!-- App-wide confirm dialog (any view can call useConfirmStore().ask). -->
    <ConfirmModal />
    <!-- App-wide single-line prompt (replaces window.prompt()). -->
    <PromptModal />
    <!-- Transient notifications (replaces native alert()). -->
    <ToastStack />
    <!-- Full-screen image viewer (click an inline image to expand). -->
    <Lightbox />
    <!-- Quick-ask popover (inline fast-lane answers; one palette command). -->
    <QuickAsk />
    <!-- Command palette (Cmd/Ctrl-K): search + run commands across the app. -->
    <CommandPalette />
  </div>
</template>

<style scoped lang="scss">
@layer layout {
  .shell {
    /* No container-type: the off-canvas drawer (below) must be positioned
       against the VIEWPORT, not against `.shell`, so with viewport-fit=cover
       it can extend into the safe areas (under the notch, down to the address
       bar). A container-type / transform here would make `.shell` the
       containing block for the fixed drawer and leave a gap at the edges.
       The off-screen parked drawer is `position: fixed`, which doesn't add
       document scroll, so no clip is needed. Phone rules use @media (a plain
       breakpoint), so no container is required for them either. */
    display: grid;
    grid-template-columns: var(--current-nav-width, var(--nav-width)) 1fr;
    /* Animate the column resize when the user toggles or the
       breakpoint flips. dur-3 + ease-out matches the rest of the
       shell motion (link hovers etc.). */
    transition: grid-template-columns var(--dur-3) var(--ease-out);
    /* Single row pinned to 100dvh so .shell__main resolves to a
       definite height; children that want height: 100% can rely on
       this chain. */
    grid-template-rows: 100dvh;
    height: 100dvh;
    overflow: hidden;

    &__nav  {
      overflow-y: auto;
    }
    &__main {
      background: var(--color-bg);
      min-height: 0;
      /* main is a flex column so .shell__content can use flex: 1
         to fill regardless of whether the route opted into full
         bleed or kept the centered max-width layout. */
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    /* Fixed header row - holds the teleported PageHeader bar. Mirrors
       the content's inline padding so the bar's negative-margin bleed
       cancels out and its bottom border runs full width to meet the
       sidebar seam. Does NOT scroll; the scrollbar lives in __content. */
    &__header {
      flex-shrink: 0;
      padding-inline: var(--space-5);
    }
    &__content {
      flex: 1;
      min-height: 0;
      width: 100%;
      /* Scrolls below the fixed header. Top padding gives a gap under
         the header bar; views that want a reading-width cap apply it on
         their own inner container (e.g. Studio's 768px chat column). The
         bottom padding adds the home-indicator inset so the last content
         clears it (0 on devices without a safe area). */
      padding: var(--space-5) var(--space-5) calc(var(--space-7) + env(safe-area-inset-bottom, 0px));
      overflow-y: auto;
    }
  }

  /* Phones: trim the content gutters so a 390px viewport isn't spending
     40px of its width on padding. The header row mirrors the inline value
     so its bottom border still lines up with the content edge. */
  @media (max-width: 479.98px) {
    /* Phone: content is full-width (the nav is an overlay), so its left/right
       edges are at the viewport edges - add the landscape rounded-corner
       insets alongside the home-indicator inset. */
    .shell__content {
      padding:
        var(--space-4)
        calc(var(--space-4) + env(safe-area-inset-right, 0px))
        calc(var(--space-6) + env(safe-area-inset-bottom, 0px))
        calc(var(--space-4) + env(safe-area-inset-left, 0px));
    }
    .shell__header { padding-inline: var(--space-4); }
  }
  /* Full-bleed wrapper: the route component lands inside this relative,
     flex:1 box (not directly in `.shell__main`) so a view sized
     `position: absolute; inset: 0` (Documents, Email message) or
     `height: 100%` (Studio) resolves against the area BELOW the optional
     phone top bar. On desktop there's no bar, so the box == `.shell__main`
     and behaviour is unchanged. */
  .shell__bleed {
    position: relative;
    flex: 1 1 0;
    min-height: 0;
    min-width: 0;
  }
  /* Kept as a redundant positioning context; `.shell__bleed` is now the
     closer one, but some full-bleed view may still expect main to be
     relative. Harmless either way. */
  .shell__main.shell__main--full {
    position: relative;
  }

  /* ── Phone top bar ──────────────────────────────────────────────
     Rendered only when sidebar.isMobile (below --bp-sm). The single
     on-screen entry to navigation + search once the nav is a drawer. */
  .shell__mobilebar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    /* Grow by the top safe-area inset and pad the content row down past the
       notch; the bar's background then fills the notch strip (border-box, so
       the row below the padding stays --header-h tall). Inline insets keep
       the buttons clear of landscape rounded corners. */
    height: calc(var(--header-h) + env(safe-area-inset-top, 0px));
    padding-top: env(safe-area-inset-top, 0px);
    padding-left: calc(var(--space-2) + env(safe-area-inset-left, 0px));
    padding-right: calc(var(--space-2) + env(safe-area-inset-right, 0px));
    border-bottom: 1px solid var(--border-subtle);
    background: var(--color-bg);
  }
  .shell__mobilebar-title {
    flex: 1;
    min-width: 0;
    text-align: center;
    font-family: var(--font-display);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .shell__mobilebar-btn {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border: 0;
    border-radius: var(--radius-button);
    background: transparent;
    color: var(--ink-primary);
    cursor: pointer;
    transition: background var(--dur-1) var(--ease-out);

    &:hover { background: var(--surface-sunken); }
    &:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 2px; }
  }

  /* ── Phones (< --bp-sm) ─────────────────────────────────────────
     The nav column collapses to nothing; the nav lives in NavDrawer (a
     Reka Dialog sheet portalled to <body>). Single grid column so the
     content fills the width. */
  @media (max-width: 479.98px) {
    .shell {
      grid-template-columns: 1fr;
    }
  }
}
</style>
